import React, { useCallback } from 'react';

import { useMatchMedia } from '@betterment/js-runtime';
import {
  Box,
  Button,
  DropdownMenu,
  H2,
  HStack,
  Icon,
  Link,
  ScrollArea,
  Separator,
  Sheet,
  ShimmerText,
  Tag,
  Text,
  VStack,
  VisuallyHidden,
} from '@betterment/samba-react';
import { cva } from '@betterment/samba-styled-system/css';
import { noop, partition } from 'es-toolkit/compat';

import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { CurrentEmployer, Employer, LoadingStatus } from './data';
import SidebarButton, { baseUnstyledButtonStyles } from './SidebarButton';

type PlanSelectProps = {
  currentEmployer?: CurrentEmployer | null;
  reload: () => void;
  loadingStatus: LoadingStatus;
  tasksLoadingStatus: LoadingStatus;
  employers?: Employer[];
  taskCounts: Record<string, number>;
  loadEmployersWithTasks: () => void;
};

type PlanSelectItemProps = {
  variant?: 'mobile' | 'desktop';
  employer: Employer;
  isCurrent?: boolean;
  tasksLoadingStatus: LoadingStatus;
  taskCount?: number;
};

type PlanLinkProps = PlanSelectItemProps & React.ComponentPropsWithoutRef<'a'>;

const planSelectItemStyles = cva({
  base: {
    textAlign: 'left',
    borderRadius: 'medium',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  variants: {
    isCurrent: {
      true: {
        bg: 'background.neutral.active',
      },
      false: {},
    },
  },
});

const planLinkNameStyles = cva({
  base: { maxW: '236px', flexGrow: 1, marginTop: 'space.25' },
  variants: {
    variant: {
      mobile: { textStyle: 'text100' },
      desktop: { textStyle: 'text50' },
    },
  },
  defaultVariants: { variant: 'desktop' },
});

const planBadgeTagTextStyles = cva({
  variants: {
    variant: {
      mobile: { fontSize: 'fontSize.50' },
      desktop: { fontSize: 'fontSize.25' },
    },
  },
  defaultVariants: { variant: 'desktop' },
});

const LoadingPlanSelectItem = () => {
  return (
    <HStack css={{ gap: 'space.800', py: 'space.150', justifyContent: 'space-between' }}>
      <ShimmerText textStyle="text25" css={{ height: '12px', my: 'space.50', width: '50%' }} />
      <ShimmerText textStyle="text25" css={{ height: '12px', my: 'space.50', width: 'size.300' }} />
    </HStack>
  );
};

const PlanBadge = ({ tasksLoadingStatus, taskCount, variant }: PlanSelectItemProps) => {
  const isLoading = tasksLoadingStatus === 'loading';

  if (isLoading) {
    return (
      <ShimmerText
        textStyle={variant === 'mobile' ? 'text50' : 'text25'}
        css={{ height: '12px', my: 'space.50', width: 'size.200' }}
      />
    );
  }

  if (taskCount !== undefined && taskCount > 0) {
    return (
      <Tag accent="blue">
        <Tag.Text className={planBadgeTagTextStyles({ variant })}>{taskCount}</Tag.Text>
      </Tag>
    );
  }

  return null;
};

const isActive = (employer: Employer | null) => {
  return employer?.plan?.status === 'ACTIVE' || employer?.plan?.status === 'ONBOARDING';
};

const PlanLink = React.forwardRef<HTMLAnchorElement, PlanLinkProps>(
  ({ employer, tasksLoadingStatus, taskCount, variant = 'desktop', isCurrent = false, ...rest }, forwardedRef) => {
    // NOTE: Because we are forwarding ref here, we cannot use SambaAutoLink as it does not
    // currently forward refs.
    return (
      <Link
        ref={forwardedRef}
        {...rest}
        variant="ghostSecondary"
        href={urlBuilder.currentEmployer({ employerId: employer.id })}
        className={planSelectItemStyles({ isCurrent })}
        css={{ p: 'space.100', minH: 0, alignItems: 'center' }}
      >
        <Text className={planLinkNameStyles({ variant })}>{employer.name}</Text>
        <PlanBadge
          variant={variant}
          employer={employer}
          tasksLoadingStatus={tasksLoadingStatus}
          taskCount={taskCount}
        />
      </Link>
    );
  }
);

const InactivePlanLink = ({ employer }: { employer: Employer }) => (
  <VStack
    css={{
      borderRadius: 'medium',
      alignItems: 'flex-start',
      bg: 'background.neutral.active',
      p: 'space.100',
      minH: 0,
    }}
  >
    <Text>{employer.name}</Text>
    <Text variant="text25">Inactive</Text>
  </VStack>
);

const ErrorButton = ({ reload }: { reload: () => void }) => (
  <HStack css={{ gap: 'space.100', px: 'space.150', mb: 'space.200' }}>
    <Icon size="small" name="ExclamationCircle" />
    <Text variant="text50">
      Something went wrong
      <br />
      <Button variant="textPrimary" onClick={reload}>
        Try again
      </Button>
    </Text>
  </HStack>
);

// Desktop variant

const DesktopPlanSelect = ({
  currentEmployer,
  employers,
  taskCounts,
  loadingStatus,
  tasksLoadingStatus,
  reload,
  loadEmployersWithTasks,
}: PlanSelectProps) => {
  const [activeEmployers, inactiveEmployers] = partition(employers || [], isActive);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && tasksLoadingStatus === 'idle') {
        loadEmployersWithTasks();
      }
    },
    [tasksLoadingStatus, loadEmployersWithTasks]
  );

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger asChild={true}>
        <SidebarButton
          label="Select plan"
          data-track-event="ElementClicked"
          data-track-name="PlanSelector"
          data-track-module="Sidebar"
        >
          <SidebarButton.Content>
            <Text variant="text25" color="text.onPrimary">
              Plan
            </Text>
            <Text title={currentEmployer?.name} color="text.onPrimary" variant="text50">
              {currentEmployer?.name}
            </Text>
          </SidebarButton.Content>
        </SidebarButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        css={{
          zIndex: 5, // style-closet z-index for overlay elements
          px: 'space.200',
          pt: 'space.300',
          pb: 'space.200',
          minW: '320px',
          maxHeight: '100dvh',
          overflowY: 'auto',
        }}
        align="start"
        side="right"
      >
        <H2 variant="title100" css={{ pb: 'space.150', px: 'space.100' }}>
          Select plan
        </H2>
        {loadingStatus === 'error' && <ErrorButton reload={reload} />}
        <VStack>
          {(loadingStatus === 'loading' || loadingStatus === 'error') && (
            <>
              <LoadingPlanSelectItem />
              <LoadingPlanSelectItem />
              <LoadingPlanSelectItem />
            </>
          )}
          {currentEmployer && !isActive(currentEmployer) && (
            <>
              <DropdownMenu.Item asChild={true} onSelect={noop}>
                <InactivePlanLink employer={currentEmployer} />
              </DropdownMenu.Item>
              <Separator css={{ my: 'space.150' }} />
            </>
          )}

          {loadingStatus === 'loaded' &&
            activeEmployers.map(employer => (
              <DropdownMenu.Item key={employer.id} asChild={true} onSelect={noop}>
                <PlanLink
                  employer={employer}
                  tasksLoadingStatus={tasksLoadingStatus}
                  taskCount={taskCounts[employer.id]}
                  isCurrent={currentEmployer?.id === employer.id}
                />
              </DropdownMenu.Item>
            ))}

          {inactiveEmployers.length > 0 && (
            <>
              <Separator css={{ my: 'space.150' }} />
              <SambaAutoLink
                variant="textPrimary"
                css={{ px: 'space.100', my: 'space.50', fontSize: 'fontSize.50' }}
                to={urlBuilder.employers()}
              >
                View inactive plans ({inactiveEmployers.length})
              </SambaAutoLink>
            </>
          )}
        </VStack>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

// Mobile variant

const MobilePlanSelect = ({
  currentEmployer,
  employers,
  taskCounts,
  loadingStatus,
  tasksLoadingStatus,
  reload,
  loadEmployersWithTasks,
}: PlanSelectProps) => {
  const [activeEmployers, inactiveEmployers] = partition(employers || [], isActive);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open && tasksLoadingStatus === 'idle') {
        loadEmployersWithTasks();
      }
    },
    [tasksLoadingStatus, loadEmployersWithTasks]
  );

  return (
    <Sheet overlay="default" onOpenChange={handleOpenChange}>
      <Sheet.Trigger asChild={true}>
        <SidebarButton
          label="Select plan"
          data-track-event="ElementClicked"
          data-track-name="PlanSelector"
          data-track-module="Sidebar"
        >
          <SidebarButton.Content>
            <Text variant="text25" color="text.onPrimary">
              Plan
            </Text>
            <Text title={currentEmployer?.name} color="text.onPrimary" variant="text50">
              {currentEmployer?.name}
            </Text>
          </SidebarButton.Content>
        </SidebarButton>
      </Sheet.Trigger>
      <Sheet.Content css={{ zIndex: 5, w: '100dvw' }} variant="left" size="medium">
        <ScrollArea>
          <VStack css={{ p: 'space.300' }}>
            <Sheet.Title asChild={true}>
              <H2 variant="title200" css={{ pb: 'space.300', px: 'space.100' }}>
                Select plan
              </H2>
            </Sheet.Title>
            <VisuallyHidden>
              <Sheet.Description>Choose from your active plans.</Sheet.Description>
            </VisuallyHidden>
            {loadingStatus === 'error' && <ErrorButton reload={reload} />}
            {(loadingStatus === 'loading' || loadingStatus === 'error') && (
              <>
                <LoadingPlanSelectItem />
                <LoadingPlanSelectItem />
                <LoadingPlanSelectItem />
              </>
            )}
            {currentEmployer && !isActive(currentEmployer) && (
              <>
                <InactivePlanLink employer={currentEmployer} />
                <Separator css={{ my: 'space.150' }} />
              </>
            )}

            {loadingStatus === 'loaded' &&
              activeEmployers.map(employer => (
                <PlanLink
                  variant="mobile"
                  key={employer.id}
                  employer={employer}
                  tasksLoadingStatus={tasksLoadingStatus}
                  taskCount={taskCounts[employer.id]}
                  isCurrent={currentEmployer?.id === employer.id}
                />
              ))}

            {inactiveEmployers.length > 0 && (
              <>
                <Separator css={{ my: 'space.150' }} />
                <SambaAutoLink
                  variant="textPrimary"
                  css={{ px: 'space.100', my: 'space.50', fontSize: 'fontSize.100' }}
                  to={urlBuilder.employers()}
                >
                  View inactive plans ({inactiveEmployers.length})
                </SambaAutoLink>
              </>
            )}
          </VStack>
        </ScrollArea>
      </Sheet.Content>
    </Sheet>
  );
};

// Top-level component that picks the right variant based on viewport

const PlanSelect = (props: PlanSelectProps) => {
  const isDesktop = useMatchMedia('atLeastMedium');
  const { loadingStatus } = props;

  if (loadingStatus === 'loading') {
    return (
      <Box css={baseUnstyledButtonStyles}>
        <SidebarButton.Content>
          <VStack data-color-mode="dark" css={{ gap: 'space.50' }}>
            <ShimmerText css={{ height: '12px', width: '50px' }} textStyle="text25" />
            <ShimmerText css={{ height: '23px', width: '120px' }} textStyle="text50" />
          </VStack>
        </SidebarButton.Content>
      </Box>
    );
  }

  return isDesktop ? <DesktopPlanSelect {...props} /> : <MobilePlanSelect {...props} />;
};

export default PlanSelect;
